import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Maximize2, X } from 'lucide-react';
import { SURFACE } from './surfaces';

/**
 * One entry in `EditPanelProps.footerActions` — a fourth, opt-in footer slot
 * rendered between the secondary action and the primary action. Mirrors the
 * shape of `EditPanel`'s existing primary/secondary button props so a
 * `footerActions` entry has full parity with the three built-in slots.
 */
export interface FooterAction {
  /** Button text. */
  label: string;
  /** Called when the user clicks this action's button. */
  onClick: () => void;
  /**
   * Visual tone — mirrors `EditPanelProps.tone`, extended with `'secondary'`
   * so an entry can render with the same outlined style the existing
   * `onSecondary` button uses. Defaults to `'primary'` when omitted.
   */
  tone?: 'primary' | 'destructive' | 'secondary';
  /** When true, this action's button is disabled. */
  disabled?: boolean;
  /**
   * TL-56 — native `title` tooltip. Added for the one case a label cannot
   * carry on its own: a permanently-disabled stub action, where the tooltip is
   * the only place the reason can live (see `TransactionTypeDetailPanel`'s
   * Attributes button, ADR-013).
   */
  title?: string;
  /**
   * TL-56 — `data-testid` on this action's button. Without it a test can only
   * reach a footer action by its visible label, which couples the test to copy
   * the design team owns.
   */
  testId?: string;
}

/**
 * Consumer count, for the two prop docs below that cite it: **37** production
 * files render `<EditPanel>`, one site each (verified 2026-09-09, SC-64).
 *
 * Counted as `<EditPanel` render sites in non-test `src/**\/*.tsx`, NOT as
 * import statements — 42 files import something from this module, but five of
 * those pull only a type (`FooterAction`, `EditPanelProps`) and never render
 * the component. The comments said "32" for long enough to be wrong twice
 * over; stating the method is what keeps the next correction honest.
 */
export interface EditPanelProps {
  /** Whether the panel is mounted and visible. */
  open: boolean;
  /**
   * Entity type or action label shown in the header (e.g. "Edit Ticket Type").
   *
   * SC-33 — rendered as the small uppercase eyebrow above the record display
   * line, matching `design_system/ui_kits/console/Maintenance.jsx`'s header
   * (confirmed authoritative — `handoffs/shared_frame/README.md:261-263`).
   * When `subtitle` is omitted, the eyebrow is suppressed and `title` renders
   * once instead, as the bold display line — see `subtitle` below for why.
   */
  title: string;
  /**
   * Contextual subtitle — entity name, last-modified info, or action context.
   *
   * SC-33 — rendered as the header's primary visual weight: a large
   * (19px/900-weight) display line. When omitted, `title` takes that slot
   * instead and the eyebrow is suppressed, so the header still always shows a
   * populated bold line (never blank) but `title` renders exactly once rather
   * than twice. `Maintenance.jsx`'s own reference fallback instead repeats
   * its eyebrow text verbatim as the display line — deliberately not
   * reproduced here because this codebase already carries a named regression
   * test against a panel showing its own title twice
   * (`titleAndOkButton.test.tsx`, P0-23/unit-16).
   */
  subtitle?: string;
  /**
   * Called when the user clicks × or Cancel.
   *
   * **Optional as of SC-61.** Omitting it renders neither the × header control
   * nor the footer's Cancel button — for a panel that is dismissed by
   * something outside itself (a workspace column's own `WsGutter`, say) rather
   * than by its own chrome. Widening this from required cannot affect the 35
   * existing consumers, all of which pass it.
   */
  onClose?: () => void;
  /**
   * When provided, a ← back button is rendered in the panel header before the
   * expand / close controls. Intended for action sub-panels that layer on top
   * of a detail view so the operator can return without closing the outer panel.
   */
  onBack?: () => void;
  /** Label for the primary action button (default: "Save"). */
  primaryLabel?: string;
  /** Called when the user clicks the primary action button. */
  onPrimary?: () => void;
  /** When true, the primary action button is disabled (e.g. during in-flight save). */
  primaryDisabled?: boolean;
  /**
   * When true, the footer's primary action button is not rendered at all —
   * for hosts whose `children` already render their own complete save/cancel
   * footer (e.g. a form component with its own "OK"/"Cancel" buttons), so
   * `EditPanel`'s own primary button isn't left dangling with no handler.
   * `Cancel` (and `secondaryLabel`/`onSecondary`, if present) are unaffected.
   * Default `false` — every existing caller keeps its current footer exactly
   * as today.
   */
  hidePrimary?: boolean;
  /**
   * TL-58 — suppress the footer's Cancel button while keeping the header ×.
   *
   * `onClose` already suppresses **both** (SC-61), for a panel dismissed from
   * outside itself. This is the other half: a panel that closes via its own ×
   * but whose footer belongs to something else — Vault Management's Context
   * Actions, whose footer is a row of transaction actions, and its New
   * Transaction launcher, which has no footer at all. Named to match
   * `hidePrimary`, which does the same job on the other button.
   */
  hideCancel?: boolean;
  /**
   * TL-58 — label for the footer's Cancel button (default: `'Cancel'`).
   * "Cancel" is wrong on a read-only panel with nothing to cancel; Closed
   * Shift View's single footer button has always said "Close".
   */
  cancelLabel?: string;
  /**
   * TL-58 — `data-testid` on the footer's Cancel button. Completes the set
   * alongside `primaryTestId` (SC-61) and `closeTestId` (TL-57): a panel
   * migrating off a hand-rolled footer keeps the hook it already shipped.
   */
  cancelTestId?: string;
  /**
   * TL-58 — disables **both** close affordances, × and Cancel, together.
   *
   * For a panel with an in-flight write it must not abandon: Close Vault
   * during `submitting`, Even Exchange during `saving`. Those hosts already
   * refuse the close in their own handler, but a live-looking control that
   * silently does nothing is worse than a disabled one — the operator cannot
   * tell a busy panel from a broken one. One prop rather than two because a
   * panel that must not be closed must not be closed by either route.
   */
  closeDisabled?: boolean;
  /**
   * Label for an optional secondary footer action, rendered between the primary
   * button and Cancel (e.g. "Add" on the Load/Modify Currency Bag form — DS
   * `ui_kits/cbm/EditPanels.jsx` `LoadBagPanel`, D-055). Omit both this and
   * `onSecondary` when no secondary action applies.
   */
  secondaryLabel?: string;
  /** Called when the user clicks the secondary footer action. Renders nothing when omitted. */
  onSecondary?: () => void;
  /** When true, the secondary action button is disabled. */
  secondaryDisabled?: boolean;
  /**
   * Visual tone of the primary action button. `'destructive'` renders it with
   * `--destructive`/`--destructive-foreground` instead of `--primary` — for the
   * Approve/Unload confirm state (DS `ConfirmPanel`, ADR-021 rule 6: destructive
   * confirmations use a narrow panel variant, not a separate modal). Default 'primary'.
   */
  tone?: 'primary' | 'destructive';
  /** Form content — fields, sections, validation messages. */
  children: React.ReactNode;
  /**
   * Background override — a CSS `background` value. Defaults to
   * `SURFACE.detail`, the design system's subtle `--muted`-over-`--card`
   * tint (`design_system/ui_kits/console/ToolFrame.jsx`'s `SURFACE.detail`).
   * Every right panel is tinted by default; there is no per-tool opt-in (E7).
   *
   * To render a panel on the plain card background instead, pass
   * `SURFACE.records` — that IS the untinted surface, so no `'none'`
   * sentinel is needed.
   */
  surface?: string;
  /**
   * E8-CBM — rendered as a `ToolFrame` workspace column (`workspacePanel`)
   * rather than a right-edge slide-out drawer: fills its container, no
   * `border-left`, no width transition, and the expand (⤢) control is
   * suppressed — the column's own `WsGutter` owns collapse instead
   * (D-084/D-123). Since there is no width transition to preserve, an inline
   * panel that is `!open` renders nothing at all rather than a zero-width
   * `<aside>`. Default `false` — every existing caller renders the drawer
   * exactly as before.
   */
  inline?: boolean;
  /**
   * Fourth footer slot rendered between the secondary action and the primary
   * action, in array order — additive and optional (SC-01). Migrating any
   * specific tool's panel onto this slot is out of scope for this unit; it
   * only adds the render point.
   */
  footerActions?: FooterAction[];
  /**
   * Overrides the default (non-expanded, non-inline) panel width, which
   * otherwise comes from the `.edit-panel.open` CSS class (520px). Has no
   * effect while `expanded` or `inline`. A CSS width value (e.g. `'640px'`)
   * — a `string` rather than a `number`, since every existing width value in
   * this codebase (520px, 100%) is a unitful CSS string, not a bare number.
   * The specific width values the design system ratifies for any given tool
   * are a separate, out-of-scope concern (SC-43) — this prop only adds the
   * override point.
   */
  width?: string;
  /**
   * Open already expanded to full content width (`.full`), as if the user had
   * pressed the ⤢ toggle. Defaults to `false`, so every existing consumer still
   * opens at the 520px default. Each reopen starts at this value again, and the
   * ⤢ toggle still works from either starting state. Used by the Document
   * Designer's content-heavy drawers (data contract, capture, version history).
   */
  defaultExpanded?: boolean;
  /**
   * Opt-in host-driven dirty signal (SC-03/ADR-6). Effective dirty state is
   * `dirty ?? internalDirty` — omitting this prop (`undefined`) preserves
   * today's exact behavior: the panel's own internal bubbled-edit tracker
   * (`onChange`/`onInput` off `.edit-panel-body`) is authoritative, and none
   * of the 37 existing consumers change behavior. Explicitly passing `dirty`
   * (`true` or `false`) supersedes the internal flag for gating the
   * discard-confirmation step — this is how a host with a real
   * form-vs-initial signal (e.g. `useFormDirty`) can drive the guard.
   */
  dirty?: boolean;
  /**
   * Opt-in host discard handler (SC-03/ADR-6). When supplied, confirming a
   * discard calls `onDiscard()` **instead of** the unconditional `onClose()`
   * call — the internal `dirty`/`confirmingDiscard` state is still reset
   * first either way. Omitting this prop preserves today's behavior
   * (`onClose()` is called directly on confirm).
   */
  onDiscard?: () => void;
  /**
   * TL-58 — when false, the body drops its 1rem padding and the host owns its
   * own insets. Default `true`, so all 37 existing consumers are unchanged.
   *
   * For a panel whose content is full-bleed and cannot be inset: Close Vault's
   * counting step is a two-column layout whose left column is a 100px tab rail
   * with its own `border-r`, and a 1rem gutter around that rail reads as a
   * misalignment rather than as breathing room. A boolean rather than a
   * `bodyClassName` because a Tailwind `p-0` passed in would be racing
   * `.edit-panel-body`'s own `padding: 1rem` on layer order, which is exactly
   * the kind of thing that works locally and breaks on a build.
   */
  bodyPadded?: boolean;
  /**
   * SC-61 — extra controls rendered in the header, before the
   * back / expand / close cluster. For a panel needing its own icon action
   * (the calculator aside's "Clear count", say) that the fixed cluster cannot
   * express. Style buttons with `ep-icon-btn` to match the built-in controls.
   */
  headerActions?: React.ReactNode;
  /**
   * SC-61 — `aria-label` for the `<aside>`. Defaults to `title`, which is what
   * every existing consumer gets. Override when the accessible name must stay
   * stable while the visible title varies (the calculator aside's title flips
   * on `readOnly`; its accessible name must not).
   */
  ariaLabel?: string;
  /** SC-61 — `data-testid` on the footer's primary button, so a panel being
   *  migrated onto `EditPanel` can keep the test hook it already shipped. */
  primaryTestId?: string;
  /**
   * TL-56 — `data-testid` on the panel's own `<aside>`. Until now a consumer
   * could only mark its root by wrapping the panel or by falling back to
   * `getByRole('complementary')` plus the accessible name; a panel migrating
   * off a hand-rolled `<aside>` (which had its own testid) had no way to keep
   * it. Omitted by default, so nothing existing gains an attribute.
   */
  testId?: string;
  /**
   * TL-57 — `data-testid` on the header's close (×) button.
   *
   * Completes the set alongside `testId` and `primaryTestId` (both TL-56). A
   * panel migrating off a hand-rolled `<aside>` typically had a testid on its
   * own close button; without this, its only remaining hook is the built-in
   * `aria-label="Close panel"`, which every panel shares and so cannot
   * disambiguate when two are on screen at once.
   */
  closeTestId?: string;
  /** `aria-busy` on the panel's `<aside>` while its content loads. Omitted by default, so nothing existing gains an attribute. */
  ariaBusy?: boolean;
}

/**
 * Right-side slide-out edit panel used by all tool create/edit/duplicate flows.
 * Replaces centered modal dialogs per ADR-021. Width: 520px (default) or 100%
 * (expanded). Footer buttons are right-aligned as a group, primary rightmost:
 * Cancel · secondary (e.g. "Add") · primary — per DS `ui_kits/cbm/EditPanels.jsx`'s
 * `PanelShell` and canvas decisions log D-090. This supersedes the previously
 * shipped primary-left/Cancel-right order (see D-090 resolution, Phase 3
 * ds-phase3-editpanel chore) — the design system is authoritative over the
 * prior in-app convention here.
 */
function getFooterActionButtonClass(tone: FooterAction['tone']): string {
  if (tone === 'destructive') {
    return 'inline-flex items-center justify-center rounded-md text-sm font-medium px-4 py-2 bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50 disabled:cursor-not-allowed';
  }
  if (tone === 'secondary') {
    return 'inline-flex items-center justify-center rounded-md text-sm font-medium px-4 py-2 border border-primary text-primary bg-background hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed';
  }
  return 'inline-flex items-center justify-center rounded-md text-sm font-medium px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed';
}

export function EditPanel({
  open,
  title,
  subtitle,
  onClose,
  onBack,
  primaryLabel = 'Save',
  onPrimary,
  primaryDisabled = false,
  hidePrimary = false,
  hideCancel = false,
  cancelLabel = 'Cancel',
  cancelTestId,
  closeDisabled = false,
  bodyPadded = true,
  secondaryLabel,
  onSecondary,
  secondaryDisabled = false,
  tone = 'primary',
  children,
  surface = SURFACE.detail,
  inline = false,
  footerActions,
  width,
  defaultExpanded = false,
  dirty: dirtyProp,
  onDiscard,
  headerActions,
  ariaLabel,
  primaryTestId,
  testId,
  closeTestId,
  ariaBusy,
}: EditPanelProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  /**
   * Discard-confirmation guard (ADR-1, findings §0 L3/L7): internal-only dirty
   * tracking so every consumer inherits the guard with zero code changes — no
   * per-tool opt-in prop required. `internalDirty` flips true on the first
   * bubbled change/input from `children`; `confirmingDiscard` gates the
   * inline confirm step (ADR-021 rule 6 — no modal).
   *
   * ADR-6 (editpanel-discard-guard-foundation): the opt-in `dirty`/`onDiscard`
   * props compose with — rather than replace — this internal mechanism.
   * Effective dirty signal is `dirty ?? internalDirty`: an omitted `dirty`
   * prop keeps this internal tracker authoritative (today's exact behavior,
   * unchanged for all 37 current consumers); an explicit `dirty` prop
   * (`true` or `false`) supersedes it.
   */
  const [internalDirty, setInternalDirty] = useState(false);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const effectiveDirty = dirtyProp ?? internalDirty;

  // Reset expansion whenever the panel opens or closes so it always starts at
  // `defaultExpanded` (false = the default width) — a user's ⤢ toggle does not
  // outlive the open it was made in.
  useEffect(() => {
    setExpanded(defaultExpanded);
  }, [open, defaultExpanded]);

  // Reset the discard-guard flags: a freshly (re)opened panel starts clean, and
  // closing defensively clears any stuck confirm state.
  useEffect(() => {
    if (!open) {
      setConfirmingDiscard(false);
    } else {
      setInternalDirty(false);
      setConfirmingDiscard(false);
    }
  }, [open]);

  // Ancestor onChange/onInput handler on .edit-panel-body: React's synthetic
  // event system bubbles these through the component tree (including portals),
  // so this one handler catches every native input/textarea/select edit and
  // every shadcn/Radix Checkbox/Switch/RadioGroup/Select interaction inside
  // children, with no per-control wiring needed (ADR-1).
  const markDirty = () => setInternalDirty(true);

  /**
   * SC-61 — whether the footer bar has anything to render. Previously it
   * always did, because Cancel was unconditional; now that `onClose` is
   * optional, a read-only panel with `hidePrimary` and no secondary/extra
   * actions would otherwise paint an empty bordered strip.
   *
   * TL-58 — `hideCancel` is a second way for the Cancel slot to be empty, so
   * it has to gate the same term `onClose` does. Missing it here is how a
   * launcher panel with no footer at all still paints the bordered strip.
   */
  const showCancel = Boolean(onClose) && !hideCancel;
  const hasFooter =
    showCancel || Boolean(onSecondary) || (footerActions?.length ?? 0) > 0 || !hidePrimary;

  const handleRequestClose = useCallback(() => {
    if (effectiveDirty) {
      setConfirmingDiscard(true);
    } else {
      onClose?.();
    }
  }, [effectiveDirty, onClose]);

  const handleDiscardChanges = () => {
    setInternalDirty(false);
    setConfirmingDiscard(false);
    if (onDiscard) {
      onDiscard();
    } else {
      onClose?.();
    }
  };

  const handleKeepEditing = () => {
    setConfirmingDiscard(false);
  };

  // IC-506 — Esc dismisses the panel, mirroring the header × / footer Cancel
  // button: both already route through `handleRequestClose`, so the
  // discard-guard and `closeDisabled` are respected identically here. Skipped
  // entirely when there's no `onClose` at all (SC-61 — a panel dismissed from
  // outside itself has no keyboard dismissal to offer either). Bails out when
  // a `<dialog>` is focused so it doesn't fight that dialog's own Escape
  // handling — see D-110 (Currency Bag Management).
  useEffect(() => {
    if (!open || closeDisabled || !onClose) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (e.target instanceof Element && e.target.closest('dialog')) return;
      handleRequestClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, closeDisabled, onClose, handleRequestClose]);

  // Inline mode has no width transition to preserve while closed — unlike
  // the drawer (kept mounted at width:0 for its CSS transition), it simply
  // isn't rendered.
  if (inline && !open) return null;

  let panelClass = 'edit-panel';
  if (inline) {
    panelClass += ' inline';
  } else if (open) {
    panelClass += expanded ? ' full' : ' open';
  }

  return (
    <aside
      className={panelClass}
      aria-label={ariaLabel ?? title}
      data-testid={testId}
      aria-busy={ariaBusy ? true : undefined}
      data-surface={surface === SURFACE.detail ? 'detail' : 'custom'}
      style={{
        background: surface,
        /*
         * `width` overrides ONLY the default open, non-expanded, non-inline
         * case — the 520px `.edit-panel.open` rule. `expanded`/`inline` keep
         * their 100% rules, governed entirely by the `.full`/`.inline`
         * classes.
         *
         * `open` is load-bearing and was missing. A closed drawer is sized by
         * `.edit-panel:not(.inline) { width: 0 }`, and an inline style beats
         * any stylesheet rule — so a closed panel carrying this override
         * rendered at its full open width, permanently. Vault Management
         * opened showing four such panels side by side instead of its grid.
         * The prop shipped with no consumers (SC-01), so the defect only
         * became reachable once TL-58 started passing it.
         */
        ...(width && open && !expanded && !inline ? { width } : {}),
      }}
    >
      <div className="edit-panel-hdr">
        <div className="edit-panel-hdr-text">
          {/*
            SC-33 — header weight inversion. `Maintenance.jsx` (the confirmed
            authoritative shared-frame reference) leads with a small uppercase
            eyebrow (the action) over a large, bold record display name;
            shipped code previously did the inverse. `title` (the action
            label) is now the eyebrow; `subtitle` (the record identifier) is
            now the large/bold line.

            When `subtitle` is omitted, the reference's own fallback repeats
            the eyebrow text verbatim as the display line — but this codebase
            already has a named regression test for a panel showing its own
            title twice (`titleAndOkButton.test.tsx`, P0-23/unit-16), so
            literal duplication is deliberately NOT reproduced here: the
            eyebrow is suppressed and `title` renders once, as the bold
            display line. This still satisfies the reference's real intent
            (never a blank header) without resurrecting a defect class this
            repo has already fixed once.
          */}
          {subtitle && (
            <span className="text-xs uppercase tracking-wider text-muted-foreground">
              {title}
            </span>
          )}
          <span className="font-barlow text-lg font-black leading-none text-foreground">
            {/*
              `||`, not `??`: several consumers pass `subtitle={mode === 'edit'
              ? form.name : undefined}`, where `form.name` starts as `''`
              (not `undefined`) before the record's data has loaded. `??`
              would treat that empty string as "a subtitle was supplied" and
              render a blank display line; `||` correctly falls back to
              `title` for both "omitted" and "loaded but still empty" alike,
              matching the same falsy check the eyebrow above already uses.
            */}
            {subtitle || title}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {headerActions}
          {onBack && (
            <button
              type="button"
              className="ep-icon-btn"
              onClick={onBack}
              aria-label="Back to detail"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          {!inline && (
            <button
              type="button"
              className="ep-icon-btn"
              onClick={() => setExpanded((e) => !e)}
              aria-label="Toggle full width"
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          )}
          {onClose && (
            <button
              type="button"
              className="ep-icon-btn"
              onClick={handleRequestClose}
              disabled={closeDisabled}
              aria-label="Close panel"
              data-testid={closeTestId}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div
        className={bodyPadded ? 'edit-panel-body' : 'edit-panel-body no-pad'}
        onChange={markDirty}
        onInput={markDirty}
      >
        {/*
          `hidden` (not a ternary) keeps `children` mounted while the confirm
          step is shown — a ternary would unmount/remount the real form on
          "Keep Editing" and silently reset it, recreating the exact defect
          this guard exists to prevent (ADR-1).
        */}
        <div hidden={confirmingDiscard}>{children}</div>
        {confirmingDiscard && (
          <div className="flex flex-col items-center justify-center gap-4 px-4 py-12 text-center">
            <p className="text-sm text-foreground">
              You have unsaved changes. Discard them?
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-md text-sm font-medium px-4 py-2 bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleDiscardChanges}
                data-testid="edit-panel-discard"
              >
                Discard Changes
              </button>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-md text-sm font-medium px-4 py-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground"
                onClick={handleKeepEditing}
                data-testid="edit-panel-keep-editing"
              >
                Keep Editing
              </button>
            </div>
          </div>
        )}
      </div>

      {!confirmingDiscard && hasFooter && (
        <div className="edit-panel-footer">
          {/* SC-61 — Cancel is the `onClose` affordance, so a panel dismissed
              from outside itself (no `onClose`) renders neither. TL-58 —
              `hideCancel` drops just this one, keeping the header ×. */}
          {showCancel && (
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md text-sm font-medium px-4 py-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleRequestClose}
              disabled={closeDisabled}
              data-testid={cancelTestId}
            >
              {cancelLabel}
            </button>
          )}
          {onSecondary && (
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md text-sm font-medium px-4 py-2 border border-primary text-primary bg-background hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={onSecondary}
              disabled={secondaryDisabled}
            >
              {secondaryLabel}
            </button>
          )}
          {footerActions && footerActions.length > 0 &&
            footerActions.map((action) => (
              <button
                key={action.label}
                type="button"
                className={getFooterActionButtonClass(action.tone)}
                onClick={action.onClick}
                disabled={action.disabled}
                title={action.title}
                data-testid={action.testId}
              >
                {action.label}
              </button>
            ))}
          {!hidePrimary && (
            <button
              type="button"
              className={
                tone === 'destructive'
                  ? 'inline-flex items-center justify-center rounded-md text-sm font-medium px-4 py-2 bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50 disabled:cursor-not-allowed'
                  : 'inline-flex items-center justify-center rounded-md text-sm font-medium px-4 py-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed'
              }
              onClick={onPrimary}
              disabled={primaryDisabled}
              data-testid={primaryTestId}
            >
              {primaryLabel}
            </button>
          )}
        </div>
      )}
    </aside>
  );
}
